import { IContext } from "@src/context";
import { defineMessages } from "@formatjs/intl";
import singleUrlHelper from "@src/utils/url";
import { ifPetLanding } from "@src/utils/componentChecker";
import { vhmsearch, vhmptypes } from "@src/proto/jsonapi";
import { jsonFetch } from "@src/utils/fetch";
import { mergeCityGroupName } from "../../components/SearchPopup/mergeCityGroupName";

import "./index.css";

import { anaClickSearch } from "./analytic";

const messages = defineMessages({
  propertyTitle: {
    id: "Autocomplete.Property.Title",
    defaultMessage: "Village House {name}",
    description: "The property title for autocomplete",
  },
});

export function setupAutocomplete(context: IContext) {
  const inputs: NodeListOf<HTMLInputElement> = document.querySelectorAll(
    'input[data-autocomplete="freesearch"]'
  );
  const api = "/api/search.json";
  const filterPet = ifPetLanding();

  if (inputs.length > 0) {
    inputs.forEach((input) => {
      const container = document.createElement("div");
      container.classList.add("element-autocomplete-freesearch");
      container.setAttribute("data-empty", "");
      input.parentElement?.appendChild(container);

      input.addEventListener("focus", function () {
        container.setAttribute("data-focus", "");
      });
      input.addEventListener("blur", function () {
        container.removeAttribute("data-focus");
      });
      // prevent input from losing focus when clicking on the container
      container.addEventListener("mousedown", function (event: Event) {
        event.preventDefault();
      });

      jsonFetch<vhmsearch.Search>(api)
        .then((res) => {
          const data = res?.indices || {};
          const basePath = singleUrlHelper.getPLP(context.locale);
          let result: vhmsearch.Search.IIndices = {};
          let activeIndex = -1;

          if (!data) {
            return;
          }

          // add input event listener
          let isComposing = false;
          input.addEventListener("compositionstart", () => {
            isComposing = true;
          });
          input.addEventListener("compositionend", function (event: Event) {
            isComposing = false;
            const value: string = (event.target as HTMLInputElement).value;
            updateInput(value);
          });
          input.addEventListener("input", function (event: Event) {
            if (!isComposing) {
              const value: string = (event.target as HTMLInputElement).value;
              updateInput(value);
            }
          });

          // add keydown event listener
          input.addEventListener("keydown", (event: KeyboardEvent) => {
            const options: NodeListOf<HTMLAnchorElement> =
              container.querySelectorAll("a");

            if (event.key === "ArrowUp") {
              event.preventDefault();
              activeIndex =
                activeIndex > 0 ? activeIndex - 1 : options.length - 1;
              highlightOption(options);
            } else if (event.key === "ArrowDown") {
              event.preventDefault();
              activeIndex =
                activeIndex < options.length - 1 ? activeIndex + 1 : 0;
              highlightOption(options);
            } else if (event.key === "Enter") {
              event.preventDefault();
              if (activeIndex === -1) {
                openSearchResultList();
              } else {
                openLink(options[activeIndex]);
              }
            }
          });

          // replace link click event
          container.addEventListener("click", (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const link = target.closest("a[data-type]") as HTMLAnchorElement;
            if (link) {
              event.preventDefault();
              openLink(link);
            }
          });

          // add submit button event listener
          const submit = container
            .closest(".container-instance")
            ?.querySelector(
              '[data-autocomplete-enter="freesearch"]'
            ) as HTMLElement;
          submit?.addEventListener("click", (event: MouseEvent) => {
            event.preventDefault();
            openSearchResultList();
          });

          function filterName(
            key: keyof vhmsearch.Search.IIndices,
            value: string
          ): object[] {
            if (value && data[key]) {
              const sub = value.toLowerCase();
              return (data[key] as vhmsearch.IProperty[])
                .filter((item: vhmsearch.IProperty) => {
                  return Object.values(item.names || {}).some(
                    (item: string) => {
                      return item.toLowerCase().includes(sub);
                    }
                  );
                })
                .filter((item: vhmsearch.IProperty) => {
                  return key === "properties" ? !filterPet || item.pet : true;
                })
                .slice(0, 3);
            } else {
              return [];
            }
          }

          function filterData(value: string): vhmsearch.Search.IIndices {
            return {
              properties: filterName("properties", value),
              prefectures: filterName("prefectures", value),
              cities: filterName("cities", value),
              trainLines: filterName("trainLines", value),
              trainStations: filterName("trainStations", value),
            } as vhmsearch.Search.IIndices;
          }

          function findMatchName(
            names: vhmptypes.II18n | null | undefined,
            value: string
          ): { language: keyof vhmptypes.II18n; name: string } | null {
            if (!names) {
              return null;
            }
            const sub = value.toLowerCase();
            const match = Object.entries(names).find((entry) => {
              return entry[1].toLowerCase().includes(sub);
            });
            if (match) {
              const [language, name] = match;
              return {
                language: language as keyof vhmptypes.II18n,
                name,
              };
            } else {
              return null;
            }
          }

          function findMatchId(
            key: keyof vhmsearch.Search.IIndices,
            id: number | string | null | undefined
          ) {
            if (id && data[key]) {
              const match = (data[key] as { id: number | string }[]).find(
                (item) => {
                  return item.id == id;
                }
              );
              if (match) {
                return match;
              } else {
                return null;
              }
            }
          }

          function highlightText(text: string, hightlight: string): string {
            return text.replace(
              new RegExp(hightlight, "i"),
              `<strong>$&</strong>`
            );
          }

          function openLink(anchor: HTMLAnchorElement | undefined) {
            const link = anchor?.getAttribute("href");
            if (anchor && link) {
              anaClickSearch(anchor, input);
              if (anchor.getAttribute("target") === "_blank") {
                window.open(link, "_blank");
              } else {
                window.location.href = link;
              }
            }
          }

          function convertUrl(relativeUrl: string): string {
            const url = new URL(relativeUrl, window.location.origin);
            if (filterPet) {
              url.searchParams.set("pet", "1");
            }
            return decodeURIComponent(url.pathname + url.search);
          }

          function generateLinks(
            result: vhmsearch.Search.IIndices,
            value: string
          ): string[] {
            const html: string[] = [];

            result.properties?.forEach((item: vhmsearch.IProperty) => {
              const name = findMatchName(item.names, value);
              const city: vhmsearch.ICity = findMatchId(
                "cities",
                item.cid
              ) as vhmsearch.ICity;
              const prefecture: vhmsearch.IPrefecture = findMatchId(
                "prefectures",
                city?.pid
              ) as vhmsearch.IPrefecture;
              const region: vhmsearch.IRegion = findMatchId(
                "regions",
                prefecture?.rid
              ) as vhmsearch.IRegion;
              const url =
                [
                  basePath,
                  region.path,
                  prefecture.path,
                  city.path,
                  item.path,
                ].join("/") + "/";

              if (name && city && prefecture) {
                const title = highlightText(name.name, value);
                const address = [
                  city.names?.[name.language] ?? city.names?.jaJp,
                  prefecture.names?.[name.language] ?? prefecture.names?.jaJp,
                ].join(", ");
                html.push(
                  `<a href="${url}" data-type="property"><span>${context?.intl.formatMessage(
                    messages.propertyTitle,
                    { name: title }
                  )}</span><p>${address}</p></a>`
                );
              }
            });

            result.prefectures?.forEach((item: vhmsearch.IPrefecture) => {
              const name = findMatchName(item.names, value);
              const region: vhmsearch.IRegion = findMatchId(
                "regions",
                item.rid
              ) as vhmsearch.IRegion;
              const url = [basePath, region.path, item.path].join("/") + "/";
              if (name && region) {
                html.push(
                  `<a href="${convertUrl(url)}" data-type="location"><strong>${
                    name.name
                  }</strong></a>`
                );
              }
            });

            result.cities?.forEach((item: vhmsearch.ICity) => {
              const name = findMatchName(item.names, value);
              const prefecture: vhmsearch.IPrefecture = findMatchId(
                "prefectures",
                item.pid
              ) as vhmsearch.IPrefecture;
              const region: vhmsearch.IRegion = findMatchId(
                "regions",
                prefecture?.rid
              ) as vhmsearch.IRegion;
              const url =
                [basePath, region.path, prefecture.path, item.path].join("/") +
                "/";
              if (name && prefecture && region) {
                // ja: `city, prefecture`, other: `city`
                const title = item.dtl
                  ? mergeCityGroupName(
                      name.name,
                      item.dtl[name.language],
                      name.language.slice(0, 2)
                    )
                  : name.language.startsWith("jaJp")
                  ? [
                      name.name,
                      prefecture.names?.[name.language] ??
                        prefecture.names?.jaJp,
                    ].join(", ")
                  : name.name;
                html.push(
                  `<a href="${convertUrl(
                    url
                  )}" data-type="location"><strong>${title}</strong></a>`
                );
              }
            });

            result.trainLines?.forEach((item: vhmsearch.ITrainLine) => {
              const name = findMatchName(item.names, value);
              const url = `${basePath}/?type=Trainline&codes=${item.id}`;
              if (name && url) {
                const title = highlightText(name.name, value);
                html.push(
                  `<a href="${convertUrl(
                    url
                  )}" data-type="trainline"><span>${title}</span></a>`
                );
              }
            });

            result.trainStations?.forEach((item: vhmsearch.ITrainStation) => {
              const name = findMatchName(item.names, value);
              const prefecture: vhmsearch.IPrefecture = findMatchId(
                "prefectures",
                item.pid
              ) as vhmsearch.IPrefecture;
              const region: vhmsearch.IRegion = findMatchId(
                "regions",
                prefecture?.rid
              ) as vhmsearch.IRegion;
              const url =
                [basePath, region.path, prefecture.path, item.path].join("/") +
                "/";
              if (name && prefecture && region) {
                const title = highlightText(name.name, value);
                const address = [
                  prefecture.names?.[name.language] ?? prefecture.names?.jaJp,
                  item.pstl,
                ].join(", ");

                html.push(
                  `<a href="${convertUrl(
                    url
                  )}" data-type="trainstation"><span>${title}</span><p>${address}</p></a>`
                );
              }
            });

            return html;
          }

          function highlightOption(
            options: NodeListOf<HTMLAnchorElement>
          ): void {
            options.forEach((option: HTMLAnchorElement, index: number) => {
              if (index === activeIndex) {
                option.setAttribute("data-active", "");
              } else {
                option.removeAttribute("data-active");
              }
            });
          }

          function updateInput(value: string) {
            result = filterData(value);
            const links = generateLinks(result, value);
            if (links.length > 0) {
              container.innerHTML = links.join("");
              container.removeAttribute("data-empty");
            } else {
              container.innerHTML = "";
              container.setAttribute("data-empty", "");
            }
            activeIndex = -1;
          }

          function openSearchResultList() {
            let params = "";
            if (result?.properties?.length) {
              params += `&properties=${result.properties
                .map((item) => item.id)
                .join(",")}`;
            }
            if (result?.prefectures?.length) {
              params += `&prefectures=${result.prefectures
                .map((item) => item.id)
                .join(",")}`;
            }
            if (result?.cities?.length) {
              params += `&cities=${result.cities
                .map((item) => item.id)
                .join(",")}`;
            }
            if (result?.trainLines?.length) {
              params += `&trainlines=${result.trainLines
                .map((item) => item.id)
                .join(",")}`;
            }
            if (result?.trainStations?.length) {
              params += `&stations=${result.trainStations
                .map((item) => item.id)
                .join(",")}`;
            }
            if (params) {
              location.href = convertUrl(`${basePath}/?type=search${params}`);
            }
          }
        })
        .catch((err) => {
          context?.errorNotifier.notify(err);
        });
    });
  }
}
